import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import * as workspaceService from '../services/workspaceService';
import type { WorkspaceBranding } from '../services/workspaceService';
import { loadGoogleFont } from '../utils/fontLoader';
import { generateBrandPalette } from '../utils/colorUtils';

interface WorkspaceContextType {
  branding: WorkspaceBranding | null;
  loading: boolean;
  error: string | null;
  refreshBranding: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [branding, setBranding] = useState<WorkspaceBranding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadBranding = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await workspaceService.getWorkspaceBranding();
      setBranding(data);
    } catch (err: any) {
      console.error('Failed to load workspace branding:', err);
      setError(err.message || 'Failed to load workspace branding');
      // Set default branding on error
      setBranding({
        workspace_name: user.name ? `${user.name}'s Workspace` : 'My Workspace',
        workspace_slug: null,
        workspace_logo_url: null,
        workspace_primary_color: '#18181b',
        workspace_accent_color: null,
        workspace_font_family: 'Inter',
        theme_dark_bg: '#09090b',
        theme_dark_heading: '#ffffff',
        theme_light_bg: '#ffffff',
        theme_light_heading: '#18181b'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBranding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Inject CSS custom properties and load font when branding changes
  useEffect(() => {
    if (!branding) return;

    const root = document.documentElement;
    const primaryColor = branding.workspace_primary_color || '#18181b';

    // Generate full shade palette from brand color and inject as CSS variables
    const palette = generateBrandPalette(primaryColor);
    for (const [shade, rgbString] of Object.entries(palette)) {
      root.style.setProperty(`--brand-primary-${shade}`, rgbString);
    }

    root.style.setProperty('--brand-primary', primaryColor);
    root.style.setProperty('--brand-accent', branding.workspace_accent_color || '#eab308');

    const fontFamily = branding.workspace_font_family || 'Inter';
    root.style.setProperty('--brand-font', `'${fontFamily}'`);
    loadGoogleFont(fontFamily);

    // Theme colors for light and dark modes
    root.style.setProperty('--theme-dark-bg', branding.theme_dark_bg || '#09090b');
    root.style.setProperty('--theme-dark-heading', branding.theme_dark_heading || '#ffffff');
    root.style.setProperty('--theme-light-bg', branding.theme_light_bg || '#ffffff');
    root.style.setProperty('--theme-light-heading', branding.theme_light_heading || '#18181b');
  }, [branding]);

  return (
    <WorkspaceContext.Provider
      value={{
        branding,
        loading,
        error,
        refreshBranding: loadBranding
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return context;
};
